import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { api, type AuthUser } from "../api/client";

const DEVELOPER_MODE_STORAGE_KEY = "developer_mode";

interface AuthContextType {
  /** With developer mode off, a developer's `scopes` are the admin view, so every scope gate hides developer UI. */
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<{ mfaRequired: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Merge per-user UI settings; applied locally at once, then saved. */
  updateUiPreferences: (patch: Record<string, unknown>) => Promise<void>;
  /** Holds the site developer role, whatever developer mode is set to. */
  isDeveloper: boolean;
  /** Show developer-only UI; switched off for demos. Per browser, display only: the API still allows everything. */
  developerMode: boolean;
  setDeveloperMode: (on: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [developerMode, setDeveloperModeState] = useState(
    () => localStorage.getItem(DEVELOPER_MODE_STORAGE_KEY) !== "0",
  );

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(
    async (email: string, password: string, totpCode?: string) => {
      const res = await api.login(email, password, totpCode);
      if (res.mfa_required) {
        return { mfaRequired: true };
      }
      if (res.user) {
        setUser(res.user);
      }
      return { mfaRequired: false };
    },
    [],
  );

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await api.me();
    setUser(u);
  }, []);

  const updateUiPreferences = useCallback(async (patch: Record<string, unknown>) => {
    setUser((prev) =>
      prev ? { ...prev, ui_preferences: { ...prev.ui_preferences, ...patch } } : prev,
    );
    const saved = await api.updateProfile({ ui_preferences: patch });
    setUser((prev) => (prev ? { ...prev, ui_preferences: saved.ui_preferences } : prev));
  }, []);

  const setDeveloperMode = useCallback((on: boolean) => {
    setDeveloperModeState(on);
    localStorage.setItem(DEVELOPER_MODE_STORAGE_KEY, on ? "1" : "0");
  }, []);

  const shownUser = useMemo(
    () =>
      user && !developerMode && user.scopes_without_developer
        ? { ...user, scopes: user.scopes_without_developer }
        : user,
    [user, developerMode],
  );

  return (
    <AuthContext.Provider
      value={{
        user: shownUser,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
        updateUiPreferences,
        isDeveloper: !!user?.scopes_without_developer,
        developerMode,
        setDeveloperMode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
